/**
 * DataSpecialistDashboard — Role-specific dashboard for data specialists
 * 
 * Assembles shared components relevant to the data specialist role:
 * - Queue stats and case list
 * - TopPrioritiesCard: highest-impact items needing attention
 * - DataQualityWidget: completeness score and missing documents
 * - AlertsPanel: real-time alerts for data issues
 * - ClassificationTooltip: hover explanations for AI classifications
 * 
 * Does NOT include accountant or attorney widgets.
 * Uses RoleDashboardLayout for app chrome + AgentNotification.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import RoleDashboardLayout from '../../components/RoleDashboardLayout';
import TopPrioritiesCard from '../../components/dashboard/TopPrioritiesCard';
import DataQualityWidget from '../../components/dashboard/DataQualityWidget';
import AlertsPanel from '../../components/dashboard/AlertsPanel';
import {
  Inbox,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw,
  ChevronRight,
  Search,
  Loader2,
} from 'lucide-react';

interface QueueStats {
  total_cases: number;
  needs_attention: number;
  no_action_needed: number;
  data_specialist: number;
  accountant: number;
  ai_processing: number;
  ready: number;
  client: number;
  total_value: number;
  cases_with_transactions_needing_review: number;
}

interface CaseItem {
  id: string;
  business_name: string;
  law_firm: string | null;
  event_type: string | null;
  assigned_queue: string;
  queue_reason: string;
  transactions_need_review: number;
  completion_percentage: number;
  days_stale: number;
  estimated_value: number;
  primary_action: string;
  primary_action_url: string;
}

export default function DataSpecialistDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, queueData] = await Promise.allSettled([
        api.get<QueueStats>('/api/v1/queue/stats?user_role=data_specialist', { showErrorToast: false }),
        api.get<{ cases: CaseItem[] }>('/api/v1/queue/data-specialist?page=1&page_size=50', { showErrorToast: false }),
      ]);
      if (statsData.status === 'fulfilled') setStats(statsData.value);
      if (queueData.status === 'fulfilled') setCases(queueData.value.cases || []);
    } catch (err) {
      console.warn('Failed to load data specialist dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredCases = cases.filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.business_name?.toLowerCase().includes(q) ||
      c.law_firm?.toLowerCase().includes(q) ||
      c.event_type?.toLowerCase().includes(q)
    );
  });

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);

  return (
    <RoleDashboardLayout role="data_specialist">
      <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Data Specialist Dashboard</h1>
            <p className="text-slate-500 mt-1">Review transactions, categorize data, resolve issues</p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-100">
                <Inbox className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.data_specialist || 0}</p>
                <p className="text-sm text-slate-500">My Queue</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-red-100">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.cases_with_transactions_needing_review || 0}</p>
                <p className="text-sm text-slate-500">Need Review</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-100">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.ai_processing || 0}</p>
                <p className="text-sm text-slate-500">AI Processing</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-green-100">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.ready || 0}</p>
                <p className="text-sm text-slate-500">Ready</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content: Case List + Sidebar Widgets */}
        <div className="flex gap-6">
          {/* Case List */}
          <div className="flex-1 min-w-0">
            {/* Search */}
            <div className="mb-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search cases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* Cases */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {loading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Loading cases...</p>
                </div>
              ) : filteredCases.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
                  <p className="font-medium text-slate-900">All caught up!</p>
                  <p className="text-sm text-slate-500 mt-1">No cases need your attention right now.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredCases.map((c) => {
                    const isStale = c.days_stale >= 7;
                    return (
                      <div
                        key={c.id}
                        onClick={() => {
                          localStorage.setItem('claimsync_view_role', 'data_specialist');
                          navigate(c.primary_action_url || `/report/${c.id}`);
                        }}
                        className="px-5 py-4 hover:bg-blue-50 cursor-pointer transition-colors flex items-center gap-4"
                      >
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                          isStale ? 'bg-red-500' : c.transactions_need_review > 0 ? 'bg-amber-500' : 'bg-green-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{c.business_name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {c.transactions_need_review > 0
                              ? `${c.transactions_need_review} transactions need review`
                              : c.primary_action || 'Ready'}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {c.estimated_value > 0 && (
                            <p className="text-sm font-medium text-slate-900">{formatCurrency(c.estimated_value)}</p>
                          )}
                          <p className="text-xs text-slate-400">{c.completion_percentage}%</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar: Data Specialist Widgets */}
          <div className="w-80 flex-shrink-0 space-y-4 hidden lg:block">
            <TopPrioritiesCard maxItems={5} />
            <DataQualityWidget />
            <AlertsPanel maxAlerts={8} compact showFilter={false} />
          </div>
        </div>
      </div>
    </RoleDashboardLayout>
  );
}
